import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  PlusIcon,
  ClipboardDocumentListIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { loteAfiliacionService, type EstadoLotePartido, type LoteAfiliacionResumido } from '../../services/api';
import { formatFechaCalendario, formatMarcaTiempo } from '../../utils/fechas';
import { ESTADO_LOTE, numeroCorto } from '../../utils/lotesAfiliacion';

const GRUPOS: { titulo: string; descripcion: string; estados: EstadoLotePartido[] }[] = [
  { titulo: 'Para completar', descripcion: 'Lotes que todavía tenés que terminar o corregir.', estados: ['BORRADOR', 'EN_SUBSANACION'] },
  { titulo: 'En trámite en la SEP', descripcion: 'Ya los presentaste. La SEP está verificando las fichas.', estados: ['EN_ESPERA_SEP'] },
  { titulo: 'Resueltos', descripcion: 'La SEP terminó de verificarlos.', estados: ['RESUELTO'] },
];

export default function LotesAfiliacionPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const partidoId = user?.partidoId;
  const [verDescartados, setVerDescartados] = useState(false);

  const { data: lotes = [], isLoading } = useQuery({
    queryKey: ['lotes-afiliacion', partidoId],
    queryFn: () => loteAfiliacionService.listar(partidoId!),
    enabled: !!partidoId,
  });

  const crear = useMutation({
    mutationFn: () => loteAfiliacionService.crear(partidoId!),
    onSuccess: (lote) => navigate(`/lotes-afiliacion/${lote.id}`),
  });

  if (!partidoId) {
    return (
      <div className="p-6">
        <div className="alert-info text-sm">Esta sección es para los usuarios de un partido.</div>
      </div>
    );
  }

  const ordenados = [...lotes].sort((a, b) => b.numero.localeCompare(a.numero));
  const descartados = ordenados.filter(l => l.estado === 'DESCARTADO');
  const vigentes = ordenados.filter(l => l.estado !== 'DESCARTADO');
  const abrir = (lote: LoteAfiliacionResumido) => navigate(`/lotes-afiliacion/${lote.id}`);

  return (
    <div className="space-y-8 p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mis lotes de afiliaciones</h1>
          <p className="text-gray-500">Presentá tus afiliaciones a la SEP para su registro (Art. 32, Ley XII N° 23).</p>
        </div>
        <button onClick={() => crear.mutate()} disabled={crear.isPending} className="btn-primary gap-2">
          <PlusIcon className="h-5 w-5" />
          {crear.isPending ? 'Creando…' : 'Cargar nuevo lote'}
        </button>
      </div>

      {isLoading && <p className="text-sm text-gray-500">Cargando…</p>}

      {!isLoading && vigentes.length === 0 && (
        <div className="card flex flex-col items-center px-6 py-12 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-50">
            <ClipboardDocumentListIcon className="h-6 w-6 text-primary-600" />
          </div>
          <p className="mt-4 font-medium text-gray-900">No tenés lotes en curso</p>
          <p className="mt-1 text-sm text-gray-500">Creá un lote y subí el Excel de afiliados para ver el resultado del control.</p>
        </div>
      )}

      {GRUPOS.map(grupo => {
        const delGrupo = vigentes.filter(l => grupo.estados.includes(l.estado));
        if (delGrupo.length === 0) return null;
        return (
          <section key={grupo.titulo} className="space-y-3">
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                {grupo.titulo} <span className="ml-1 text-gray-400">({delGrupo.length})</span>
              </h2>
              <p className="text-sm text-gray-400">{grupo.descripcion}</p>
            </div>
            {delGrupo.map(lote => <TarjetaLote key={lote.id} lote={lote} onAbrir={() => abrir(lote)} />)}
          </section>
        );
      })}

      {descartados.length > 0 && (
        <section className="space-y-3">
          <button
            onClick={() => setVerDescartados(v => !v)}
            className="inline-flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-700"
          >
            {verDescartados ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
            {verDescartados ? 'Ocultar' : 'Ver'} lotes descartados ({descartados.length})
          </button>
          {verDescartados && descartados.map(lote => <TarjetaDescartado key={lote.id} lote={lote} />)}
        </section>
      )}
    </div>
  );
}

function TarjetaLote({ lote, onAbrir }: { lote: LoteAfiliacionResumido; onAbrir: () => void }) {
  const estado = ESTADO_LOTE[lote.estado];
  const borrador = lote.estado === 'BORRADOR';
  const subsanacion = lote.estado === 'EN_SUBSANACION';
  const { total, sinObservaciones, conObservaciones } = lote.resumen;

  return (
    <button
      onClick={onAbrir}
      className={`card block w-full text-left transition hover:border-primary-300 hover:shadow-md ${
        borrador ? 'border-dashed' : ''
      } ${subsanacion ? 'border-l-4 border-l-amber-400' : ''}`}
    >
      <div className="flex items-start justify-between gap-4 px-6 py-4">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="text-lg font-semibold text-gray-900">Lote N° {numeroCorto(lote.numero)}</h3>
            <span className={estado.clase}>{estado.etiqueta}</span>
          </div>
          <p className="text-sm text-gray-500">
            {borrador
              ? `Creado el ${formatMarcaTiempo(lote.creadoEn)} · ${total} ${total === 1 ? 'ficha' : 'fichas'}`
              : `Presentado el ${formatFechaCalendario(lote.fechaPresentacion)} · ${total} ${total === 1 ? 'ficha' : 'fichas'}`}
          </p>
          {borrador && total > 0 && (
            <div className="flex flex-wrap gap-4 pt-1 text-sm">
              <span className="inline-flex items-center gap-1 text-green-700">
                <CheckCircleIcon className="h-4 w-4" /> {sinObservaciones} sin observaciones
              </span>
              <span className="inline-flex items-center gap-1 text-amber-700">
                <ExclamationTriangleIcon className="h-4 w-4" /> {conObservaciones} con observaciones
              </span>
            </div>
          )}
          {estado.descripcion && <p className="pt-1 text-sm text-gray-600">{estado.descripcion}</p>}
          {borrador && (
            <p className="pt-1 text-sm font-medium text-primary-600 underline underline-offset-2">
              {total === 0 ? 'Cargar fichas' : 'Continuar con este lote'}
            </p>
          )}
        </div>
        <ChevronRightIcon className="mt-1 h-5 w-5 flex-shrink-0 text-gray-400" />
      </div>
    </button>
  );
}

function TarjetaDescartado({ lote }: { lote: LoteAfiliacionResumido }) {
  return (
    <div className="card bg-gray-50 px-6 py-3">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="font-medium text-gray-500 line-through">Lote N° {numeroCorto(lote.numero)}</h3>
        <span className={ESTADO_LOTE.DESCARTADO.clase}>{ESTADO_LOTE.DESCARTADO.etiqueta}</span>
      </div>
      <p className="mt-1 text-sm text-gray-400">
        Lo descartaste el {formatMarcaTiempo(lote.descartadoEn)} sin presentarlo. Su número no se vuelve a usar.
      </p>
    </div>
  );
}
